import React, { useCallback, useEffect, useRef, useState } from 'react';
import LeaderboardPage from './LeaderboardPage';
import QuickLinks from './QuickLinks';
import HowToPlayPage from './HowToPlayPage';
import { AlreadyPlayedPage, fetchGameStatuses } from '@precisionpros/game-ui';

const installHubAuthFetch = () => {
  if (typeof window === 'undefined' || window.__hubAuthFetchInstalled) return;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || '';
    if (token) localStorage.setItem('hubToken', token);
    const headers = new Headers(init.headers || {});
    if (token) headers.set('X-Hub-Token', token);
    return originalFetch(input, { ...init, headers });
  };
  window.__hubAuthFetchInstalled = true;
};

installHubAuthFetch()

const API = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:8005/api/wordprofinder' : 'https://wordprofinder.precisionpros.com/api/wordprofinder');
const hubHeaders = () => {
  const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || '';
  if (token) localStorage.setItem('hubToken', token);
  return token ? { 'X-Hub-Token': token } : {};
};

const key = ([r, c]) => `${r}-${c}`;
const same = (a, b) => a && b && a[0] === b[0] && a[1] === b[1];
const REGULAR_GAME_LIMIT_MS = 60_000;
const SPECIAL_GAME_LIMIT_MS = 180_000;

const formatCountdown = (milliseconds) => {
  const totalSeconds = Math.ceil(Math.max(0, milliseconds) / 1000);
  return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
};

function FinderWordmark() {
  return <><span style={{ color: '#333', fontWeight: 500 }}>Word</span><span style={{ color: '#4CAF50', fontWeight: 900 }}>Pro</span><span style={{ color: '#333', fontWeight: 500 }}>Finder</span></>;
}

const WORDPRO_FINDER_GAME = { name: 'WordProFinder', wordmarkMain: 'Word', wordmarkAccent: 'Pro', wordmarkSuffix: 'Finder', badge: 'WF', accent: '#4CAF50' };

function nextCellIsValid(path, cell, flexible = false) {
  if (path.some((item) => same(item, cell))) return false;
  if (!path.length) return true;
  if (flexible) {
    const previous = path[path.length - 1];
    return Math.max(Math.abs(cell[0] - previous[0]), Math.abs(cell[1] - previous[1])) === 1;
  }
  if (path.length === 1) return Math.max(Math.abs(cell[0] - path[0][0]), Math.abs(cell[1] - path[0][1])) === 1;
  const previous = path[path.length - 1];
  const direction = [Math.sign(previous[0] - path[path.length - 2][0]), Math.sign(previous[1] - path[path.length - 2][1])];
  return cell[0] - previous[0] === direction[0] && cell[1] - previous[1] === direction[1];
}

function App() {
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || '';
    if (!token) window.location.replace(window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://games.precisionpros.com');
  }, []);
  if (window.location.pathname === '/leaderboard') return <LeaderboardPage />;
  if (window.location.pathname === '/how-to-play') return <HowToPlayPage />;
  const queryPlayerName = new URLSearchParams(window.location.search).get('player_name');
  const [playerName, setPlayerName] = useState(queryPlayerName || localStorage.getItem('playerName') || '');
  const [nameInput, setNameInput] = useState(playerName);
  const [puzzle, setPuzzle] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selection, setSelection] = useState([]);
  const [phase, setPhase] = useState('loading');
  const [playedCompleted, setPlayedCompleted] = useState(true);
  const [centralStatuses, setCentralStatuses] = useState({});
  const [message, setMessage] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [foundWords, setFoundWords] = useState([]);
  const startedAt = useRef(0);
  const actionPending = useRef(false);
  const dragActive = useRef(false);
  const dragPointerId = useRef(null);
  const dragged = useRef(false);
  const dragPath = useRef([]);
  const suppressClick = useRef(false);

  const load = useCallback(async () => {
    const [leaderboardResponse, dailyResponse, statuses] = await Promise.all([
      fetch(`${API}/leaderboard/`, { headers: hubHeaders() }),
      fetch(`${API}/daily/`),
      fetchGameStatuses().catch(() => ({})),
    ]);
    const nextLeaderboard = await leaderboardResponse.json();
    const nextPuzzle = await dailyResponse.json();
    if (!leaderboardResponse.ok) throw new Error(nextLeaderboard.error || 'Leaderboard unavailable');
    if (!dailyResponse.ok) throw new Error(nextPuzzle.error || 'Puzzle unavailable');
    setLeaderboard(nextLeaderboard.results || []);
    setPuzzle(nextPuzzle);
    setCentralStatuses(statuses);
    if (queryPlayerName) localStorage.setItem('playerName', queryPlayerName);
    if (statuses.wordprofinder === true) {
      setPhase('already-played');
      return;
    }
    setPhase('ready');
  }, [playerName, queryPlayerName]);

  useEffect(() => { load().catch((error) => { setMessage(error.message); setPhase('error'); }); }, [load]);
  const giveUp = useCallback(async (timedOut = false) => {
    if (phase !== 'playing' || actionPending.current) return;
    actionPending.current = true;
    const timeLimit = puzzle?.mode === 'multi_straight' ? SPECIAL_GAME_LIMIT_MS : REGULAR_GAME_LIMIT_MS;
    const solveTime = timedOut ? timeLimit : Math.floor(performance.now() - startedAt.current);
    try {
      const response = await fetch(`${API}/submit-result/`, {method: 'POST', headers: {'Content-Type': 'application/json', ...hubHeaders()}, body: JSON.stringify({player_name: playerName, give_up: true, solve_time_ms: solveTime})});
      const result = await response.json();
      if (!response.ok) {
        actionPending.current = false;
        setMessage(result.error || 'Unable to finish game');
        return;
      }
      setElapsed(solveTime);
      setSelection([]);
      setFoundWords([]);
      setPhase('failed');
      setMessage(timedOut ? `Time’s up — you had ${puzzle?.mode === 'multi_straight' ? '3 minutes' : '1 minute'} to finish today’s puzzle.` : 'You gave up on today’s puzzle.');
    } catch (error) {
      actionPending.current = false;
      setMessage('Unable to finish game');
    }
  }, [phase, playerName, puzzle?.mode]);

  useEffect(() => {
    if (phase !== 'playing') return undefined;
    const updateTimer = () => {
      const nextElapsed = Math.floor(performance.now() - startedAt.current);
      const timeLimit = puzzle?.mode === 'multi_straight' ? SPECIAL_GAME_LIMIT_MS : REGULAR_GAME_LIMIT_MS;
      if (puzzle?.mode !== 'flexible' && nextElapsed >= timeLimit) {
        setElapsed(timeLimit);
        giveUp(true);
      } else {
        setElapsed(nextElapsed);
      }
    };
    const timer = setInterval(updateTimer, 50);
    return () => clearInterval(timer);
  }, [giveUp, phase, puzzle?.mode]);

  const submit = async (nextSelection) => {
    if (nextSelection.length !== 5 || phase !== 'playing' || actionPending.current) return;
    const solveTime = Math.floor(performance.now() - startedAt.current);
    const timeLimit = puzzle.mode === 'multi_straight' ? SPECIAL_GAME_LIMIT_MS : REGULAR_GAME_LIMIT_MS;
    if (puzzle.mode !== 'flexible' && solveTime > timeLimit) {
      giveUp(true);
      return;
    }
    actionPending.current = true;
    try {
      const response = await fetch(`${API}/submit-result/`, {method: 'POST', headers: {'Content-Type': 'application/json', ...hubHeaders()}, body: JSON.stringify({player_name: playerName, selection: nextSelection, solve_time_ms: solveTime})});
      const result = await response.json();
      setElapsed(solveTime);
      if (result.correct) {
        if (puzzle.mode === 'flexible' || puzzle.mode === 'multi_straight') {
          if (!result.already_found) setFoundWords((current) => [...current, result.word]);
          setSelection([]);
          if (result.complete) {
            setPhase('won'); setMessage('');
            fetch(`${API}/leaderboard/`).then((res) => res.json()).then((data) => setLeaderboard(data.results || []));
          } else {
            actionPending.current = false;
            setMessage(result.already_found ? `${result.word} was already found.` : `${result.word} found!`);
            window.setTimeout(() => setMessage(''), 1400);
          }
        } else {
          setPhase('won'); setMessage('');
          fetch(`${API}/leaderboard/`).then((res) => res.json()).then((data) => setLeaderboard(data.results || []));
        }
      } else {
        actionPending.current = false;
        setSelection([]);
        setMessage(result.already_found ? `${result.word} was already found.` : 'Incorrect — try again.');
        window.setTimeout(() => setMessage(''), 1400);
      }
    } catch (error) {
      actionPending.current = false;
      setSelection([]);
      setMessage('Unable to check that word — try again.');
    }
  };

  const chooseName = (event) => {
    event.preventDefault();
    const cleaned = nameInput.trim();
    if (!cleaned) return;
    localStorage.setItem('playerName', cleaned); setPlayerName(cleaned);
  };

  const startGame = async () => {
    setPhase('loading');
    try {
      const response = await fetch(`${API}/daily/`);
      const nextPuzzle = await response.json();
      if (!response.ok) throw new Error(nextPuzzle.error || 'Puzzle unavailable');
      setPuzzle(nextPuzzle);
    } catch (error) {
      setMessage(error.message);
      setPhase('error');
      return;
    }
    setSelection([]);
    setMessage('');
    setElapsed(0);
    actionPending.current = false;
    startedAt.current = performance.now();
    setPhase('playing');
  };

  const finishDrag = () => {
    if (!dragActive.current) return;
    dragActive.current = false;
    if (dragged.current) {
      suppressClick.current = true;
      submit(dragPath.current);
      window.setTimeout(() => { suppressClick.current = false; }, 0);
    }
    dragged.current = false;
    dragPath.current = [];
  };
  useEffect(() => {
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    return () => {
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
    };
  });

  const append = (cell) => {
    const selectedIndex = selection.findIndex((item) => same(item, cell));
    if (selectedIndex >= 0) {
      setSelection(selection.slice(0, selectedIndex));
      return;
    }
    if (selection.length >= 5 || !nextCellIsValid(selection, cell, puzzle?.mode === 'flexible')) return;
    const next = [...selection, cell]; setSelection(next); if (next.length === 5) submit(next);
  };
  const handlePointerDown = (cell, event) => {
    if (phase !== 'playing') return;
    dragPointerId.current = event.pointerId;
    dragActive.current = true;
    dragged.current = false;
    dragPath.current = [cell];
    if (selection.length === 0) setSelection([cell]);
  };
  const addDragCell = (cell) => {
    if (!dragActive.current || dragPath.current.length >= 5) return;
    if (nextCellIsValid(dragPath.current, cell, puzzle?.mode === 'flexible')) {
      dragged.current = true;
      dragPath.current = [...dragPath.current, cell];
      setSelection(dragPath.current);
    }
  };
  const handlePointerEnter = (cell) => addDragCell(cell);
  const handlePointerMove = (cell, event) => {
    if (!dragActive.current || event.pointerId !== dragPointerId.current || (event.pointerType === 'mouse' && event.buttons !== 1)) return;
    addDragCell(cell);
  };
  const handleMouseMove = (cell, event) => {
    if (dragActive.current && event.buttons === 1) addDragCell(cell);
  };
  const handleTouchMove = (event) => {
    if (!dragActive.current) return;
    const touch = event.touches[0];
    const cell = document.elementFromPoint?.(touch.clientX, touch.clientY)?.closest?.('.cell');
    if (cell) addDragCell([Number(cell.dataset.row), Number(cell.dataset.col)]);
  };
  const handleClick = (cell) => { if (!suppressClick.current) append(cell); };

  if (!playerName) return <main className="shell"><section className="card name-card"><div className="brand-mark" style={{ backgroundColor: '#4CAF50', color: '#000' }}>WF</div><h1><FinderWordmark /></h1><p>Find today’s hidden WordPro solution.</p><form onSubmit={chooseName}><input autoFocus value={nameInput} onChange={(event) => setNameInput(event.target.value)} placeholder="Your name" maxLength="100"/><button>Start finding</button></form></section></main>;
  if (phase === 'loading') return <main className="shell"><div className="card">Loading today’s puzzle…</div></main>;
  if (phase === 'error') return <main className="shell"><div className="card"><h1><FinderWordmark /></h1><p>{message}</p></div></main>;
  if (phase === 'already-played') return <AlreadyPlayedPage
    game={WORDPRO_FINDER_GAME}
    title={playedCompleted ? 'You already played today!' : 'Today’s game is complete'}
    message={playedCompleted ? 'Come back tomorrow for a new puzzle.' : 'The solution is available on the leaderboard.'}
    detail={playedCompleted ? `Your solve time was ${(elapsed / 1000).toFixed(2)} seconds.` : ''}
    icon={playedCompleted ? '✅' : '×'}
    leaderboardHref="/leaderboard"
    homeHref="https://games.precisionpros.com"
  />;
  if (phase === 'ready') return <main className="shell"><header><div><div className="eyebrow">DAILY WORD GAME</div><h1><FinderWordmark /></h1></div><div className="header-actions"><a className="back-link" href="/leaderboard">Leaderboard</a><button className="link-button" onClick={() => {localStorage.removeItem('playerName'); setPlayerName('');}}>Change name</button></div></header><QuickLinks playerName={playerName} centralStatuses={centralStatuses} />
    <section className={`card ${puzzle.mode === 'flexible' ? 'flexible-ready-card' : ''}`}>
      {puzzle.mode === 'flexible' ? <>
        <div className="flexible-alert"><span className="flexible-alert-icon">↗</span><div><div className="flexible-kicker">SPECIAL GAMEPLAY TODAY</div><strong>Words can bend.</strong></div></div>
        <h2>Find the hidden WordPro words</h2>
        <p className="flexible-lead">Today’s puzzle is played differently: there are <strong>{puzzle.word_count} words</strong> to find, and none of them has to stay in a straight line.</p>
        <div className="flexible-rules"><div><b>1</b><span>Start on any letter.</span></div><div><b>2</b><span>Connect to any of the 8 surrounding cells, including diagonals.</span></div><div><b>3</b><span>Turn freely, but never use the same cell twice in one word.</span></div></div>
        <div className="flexible-goal"><strong>{puzzle.word_count}</strong><span>WordPro words to find<br/><small>The counter will show what remains.</small></span></div>
      </> : puzzle.mode === 'multi_straight' ? <>
        <div className="multi-alert"><div className="multi-alert-kicker">SPECIAL GAMEPLAY TODAY</div><strong>Find 3 hidden words in 3 minutes.</strong><span>Each WordPro daily word follows the regular rules: a straight horizontal, vertical, or diagonal line that may run forward or backward.</span></div>
        <h2>Ready to find all three words?</h2>
        <p>Trace the three hidden 5-letter WordPro daily words before the 3-minute countdown reaches zero.</p>
        <ul style={{ color: '#52635a', lineHeight: 1.7, paddingLeft: '22px' }}><li>Every answer is a scheduled WordPro daily word.</li><li>Each word appears in one straight line.</li><li>Words may run horizontally, vertically, or diagonally.</li><li>Words may run forward or backward.</li><li>You can make unlimited guesses within 3 minutes.</li></ul>
      </> : <>
        <div className="timer-update" role="note"><div className="timer-update-kicker">GAMEPLAY UPDATE</div><strong>You now have 60 seconds to find the word.</strong><span>If time runs out, the game ends as a give-up.</span></div>
        <h2>Ready to find today’s word?</h2><p>Find the WordPro 5-letter word hidden in the 7×7 grid.</p><ul style={{ color: '#52635a', lineHeight: 1.7, paddingLeft: '22px' }}><li>Every answer is an actual WordPro solution.</li><li>The word appears in one straight line: horizontal, vertical, or diagonal.</li><li>The word may run forward or backward.</li><li>Trace the letters, or tap them one at a time.</li><li>You have 1 minute and can make unlimited guesses.</li></ul>
      </>}
      <p className="muted">Your timer begins when you press Start.</p><button onClick={startGame} className={puzzle.mode === 'flexible' ? 'flexible-start-button' : ''} style={puzzle.mode === 'flexible' ? {} : { border: 0, background: 'none', color: '#16803c', padding: 0, fontWeight: 700 }}>Start finding →</button>
    </section></main>;

  const finished = phase === 'won' || phase === 'failed';
  if (finished) return <main className="shell"><header><div><div className="eyebrow">DAILY WORD GAME</div><h1><FinderWordmark /></h1></div></header>
    <section className={`card result ${phase}`}><div className="result-icon">{phase === 'won' ? '✓' : '×'}</div><h2>{phase === 'won' ? 'Found it!' : 'Not this time'}</h2><p>{phase === 'won' ? `You found ${puzzle.mode === 'multi_straight' ? 'all three words' : 'the word'} in ${(elapsed / 1000).toFixed(2)} seconds.` : message}</p><a className="back-link" href="/leaderboard">View leaderboard →</a></section>
    </main>;
  return <main className="shell"><header><div><div className="eyebrow">DAILY WORD GAME</div><h1><FinderWordmark /></h1></div><div className="header-actions"><a className="back-link" href="/leaderboard">Leaderboard</a><button className="link-button" onClick={() => {localStorage.removeItem('playerName'); setPlayerName('');}}>Change name</button></div></header><QuickLinks playerName={playerName} centralStatuses={centralStatuses} wordprofinderPlayed={phase === 'won'} />
    <section className="card game-card"><div className="game-top"><div><strong>{playerName}</strong><span className="muted"> · {puzzle.date}</span></div><div className="timer" aria-label={puzzle.mode === 'flexible' ? 'Elapsed time' : 'Time remaining'}>{puzzle.mode === 'flexible' ? `${(elapsed / 1000).toFixed(2)}s` : formatCountdown((puzzle.mode === 'multi_straight' ? SPECIAL_GAME_LIMIT_MS : REGULAR_GAME_LIMIT_MS) - elapsed)}</div></div>
      {!finished && <p className="instruction">{puzzle.mode === 'flexible' ? `Find the WordPro words by connecting adjacent letters. ${puzzle.word_count - foundWords.length} remaining.` : puzzle.mode === 'multi_straight' ? `Find 3 WordPro words in straight lines. ${puzzle.word_count - foundWords.length} remaining.` : 'Find the WordPro 5-letter word in a straight line, or tap the letters one at a time. You can keep trying until you find it.'}</p>}
      {!finished && message && <div role="status" aria-live="polite" style={{ textAlign: 'center', color: '#a15d16', fontWeight: 700, fontSize: '0.9rem', margin: '-8px 0 16px' }}>{message}</div>}
      <div className="grid">{puzzle.grid.map((row, r) => row.split('').map((letter, c) => <button key={`${r}-${c}`} data-row={r} data-col={c} className={`cell ${selection.some((item) => item[0] === r && item[1] === c) ? 'selected' : ''}`} onPointerDown={(event) => handlePointerDown([r,c], event)} onPointerMove={(event) => handlePointerMove([r,c], event)} onMouseMove={(event) => handleMouseMove([r,c], event)} onTouchMove={handleTouchMove} onPointerEnter={() => handlePointerEnter([r,c])} onClick={() => handleClick([r,c])}>{letter}</button>))}</div>
      {!finished && <button className="give-up-button" onClick={() => giveUp(false)}>I give up</button>}
    </section>
    <footer>WordProFinder · one WordPro solution hidden every day</footer></main>;
}

export default App;
